import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTheme } from "@mui/material/styles";
import { deleteWorkflow, listWorkflows } from "../../api/agentApi";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../../constants";
import { useUser } from "../UserContext.jsx";
import { PERMISSIONS } from "../../permissions";

interface Workflow {
  id: string;
  name: string;
  version: number;
  status?: string;
  created_at?: string;
  description?: string;
  category?: string;
  workflow_category?: string;
  tags?: string[];
}

const COPPER = BRAND_COLORS.primary.main;
const COPPER_DARK = BRAND_COLORS.primary.dark;
const COPPER_SOFT = BRAND_COLORS.primary.soft;
const BORDER_SOFT = "#E5D4C4";
const GREEN = "#2F7D4A";
const GREEN_SOFT = "rgba(47, 125, 74, 0.12)";
const MUTED = "#6F6A64";

const formControlSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: UI_RADIUS.input,
  },
};

const primaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  bgcolor: "primary.main",
  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
  color: "primary.contrastText",
  textTransform: "none",
  fontWeight: 900,
  minHeight: 44,
  minWidth: { sm: 170 },
  px: 2.4,
  width: { xs: "100%", sm: "auto" },
  "&:hover": {
    bgcolor: "primary.dark",
    boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
  },
};

const secondaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  borderColor: BORDER_SOFT,
  color: COPPER_DARK,
  textTransform: "none",
  fontWeight: 850,
  minHeight: 40,
  px: 2,
  "&:hover": {
    borderColor: COPPER,
    bgcolor: BRAND_COLORS.primary.softSubtle,
  },
};

const chipBaseSx = {
  borderRadius: UI_RADIUS.chip,
};

const cardBaseSx = {
  borderRadius: UI_RADIUS.card,
};

const iconTileSx = {
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};

const WORKFLOW_COLLECTIONS = [
  {
    id: "marketing_campaigns",
    label: "Marketing Campaigns",
    description: "Launches, content operations, campaign kits, and channel orchestration.",
    keywords: ["marketing", "campaign", "launch", "content", "social", "email", "brand", "seo"],
  },
  {
    id: "document_productivity",
    label: "Document Productivity",
    description: "Reports, briefs, summaries, presentations, and office deliverables.",
    keywords: ["document", "report", "brief", "summary", "ppt", "deck", "presentation", "proposal"],
  },
  {
    id: "review_approval",
    label: "Review & Approval",
    description: "Human-in-the-loop review flows, approvals, QA, and governance checkpoints.",
    keywords: ["review", "approval", "approve", "qa", "compliance", "governance", "audit"],
  },
  {
    id: "operations",
    label: "Operations",
    description: "Recurring operations, status checks, routing, support, and process automation.",
    keywords: ["ops", "operations", "support", "ticket", "status", "routing", "process"],
  },
];

const OTHER_COLLECTION = {
  id: "other_workflows",
  label: "Other Workflows",
  description: "Reusable workflows that do not yet map to a specific collection.",
  keywords: [],
};

function normalizeStatus(status?: string) {
  return String(status || "").trim().toLowerCase();
}

function getWorkflowText(workflow: Workflow) {
  return [
    workflow.name,
    workflow.description,
    workflow.category,
    workflow.workflow_category,
    ...(workflow.tags || []),
  ].filter(Boolean).join(" ").toLowerCase();
}

function inferCollection(workflow: Workflow) {
  const explicit = String(workflow.category || workflow.workflow_category || "").toLowerCase().replace(/[^a-z0-9]+/g, "_");
  const exact = WORKFLOW_COLLECTIONS.find((collection) => collection.id === explicit);
  if (exact) return exact;
  const text = getWorkflowText(workflow);
  return WORKFLOW_COLLECTIONS.find((collection) => collection.keywords.some((keyword) => text.includes(keyword))) || OTHER_COLLECTION;
}

function groupWorkflows(workflows: Workflow[]) {
  const groups = new Map<string, any>();
  workflows.forEach((workflow) => {
    const collection = inferCollection(workflow);
    const existing = groups.get(collection.id) || { ...collection, workflows: [], activeCount: 0, draftCount: 0 };
    existing.workflows.push(workflow);
    if (["active", "ready", "published"].includes(normalizeStatus(workflow.status))) existing.activeCount += 1;
    else existing.draftCount += 1;
    groups.set(collection.id, existing);
  });
  const order = [...WORKFLOW_COLLECTIONS.map((collection) => collection.id), OTHER_COLLECTION.id];
  return Array.from(groups.values()).sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
}

function CollectionIcon({ id }: { id: string }) {
  if (id === "marketing_campaigns") return <CampaignOutlinedIcon />;
  if (id === "document_productivity") return <ArticleOutlinedIcon />;
  if (id === "review_approval") return <AssignmentTurnedInOutlinedIcon />;
  return <AccountTreeOutlinedIcon />;
}

function statusChip(workflow: Workflow, isDark = false) {
  const status = normalizeStatus(workflow.status);
  if (["active", "ready", "published"].includes(status)) {
    return {
      label: status || "active",
      sx: {
        ...chipBaseSx,
        bgcolor: isDark ? "rgba(91,160,92,0.18)" : GREEN_SOFT,
        color: isDark ? "#CFEFD4" : GREEN,
        border: `1px solid ${isDark ? "rgba(145,205,153,0.30)" : "rgba(47,125,74,0.28)"}`,
      },
    };
  }
  return {
    label: status || "draft",
    sx: {
      ...chipBaseSx,
      bgcolor: isDark ? "rgba(224,177,132,0.16)" : COPPER_SOFT,
      color: isDark ? "#F2C69B" : COPPER_DARK,
      border: `1px solid ${isDark ? "rgba(224,177,132,0.30)" : "rgba(184,115,51,0.24)"}`,
    },
  };
}

function EmptyWorkflowTable({ message }: { message: string }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const borderSoft = isDark ? "rgba(224,177,132,0.24)" : BORDER_SOFT;
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: UI_RADIUS.panel,
        border: `1px solid ${borderSoft}`,
        bgcolor: isDark ? "#181411" : "#FFFEFC",
        overflow: "hidden",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: isDark ? "rgba(255,247,239,0.06)" : "#F7F2ED" }}>
            <TableCell sx={{ fontWeight: 900 }}>Workflow ID</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Version</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Created At</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary", fontWeight: 800 }}>
              {message}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function WorkflowListPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { can } = useUser();
  const canCreateWorkflows = Boolean(can?.(PERMISSIONS.WORKFLOWS_CREATE));
  const canUpdateWorkflows = Boolean(can?.(PERMISSIONS.WORKFLOWS_UPDATE));
  const canRunWorkflows = Boolean(can?.(PERMISSIONS.WORKFLOWS_RUN));
  const canDeleteWorkflows = Boolean(can?.(PERMISSIONS.WORKFLOWS_DELETE));
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  useEffect(() => {
    listWorkflows()
      .then((data: Workflow[]) => setWorkflows(data || []))
      .catch(() => setError("Failed to load workflows."));
  }, []);

  useEffect(() => {
    setSelectedCollection(null);
  }, [searchQuery, selectedStatus]);

  const handleDelete = async (workflowId: string) => {
    if (!canDeleteWorkflows) return;
    if (!window.confirm(`Delete workflow '${workflowId}'? This cannot be undone.`)) return;
    setDeleting(workflowId);
    try {
      await deleteWorkflow(workflowId);
      setWorkflows((current) => current.filter((workflow) => workflow.id !== workflowId));
    } catch (e) {
      setError("Failed to delete workflow.");
    }
    setDeleting("");
  };

  const filteredWorkflows = useMemo(() => {
    return workflows.filter((workflow) => {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        !query ||
        workflow.id.toLowerCase().includes(query) ||
        workflow.name?.toLowerCase().includes(query) ||
        getWorkflowText(workflow).includes(query);
      const status = normalizeStatus(workflow.status);
      const matchesStatus =
        selectedStatus === "all" ||
        (selectedStatus === "active" ? ["active", "ready", "published"].includes(status) : status === selectedStatus);
      return matchesSearch && matchesStatus;
    });
  }, [workflows, searchQuery, selectedStatus]);

  const groups = useMemo(() => groupWorkflows(filteredWorkflows), [filteredWorkflows]);
  const activeGroup = selectedCollection ? groups.find((group) => group.id === selectedCollection) : null;
  const visibleWorkflows = activeGroup?.workflows || filteredWorkflows;
  const panelBg = isDark ? "#181411" : "#FFFEFC";
  const panelSoftBg = isDark ? "#211B16" : "#F7F2ED";
  const borderSoft = isDark ? "rgba(224,177,132,0.24)" : BORDER_SOFT;
  const copperText = isDark ? "#F2C69B" : COPPER_DARK;
  const mutedText = isDark ? "text.secondary" : MUTED;
  const copperSurface = isDark ? "rgba(224,177,132,0.16)" : COPPER_SOFT;
  const greenSurface = isDark ? "rgba(91,160,92,0.18)" : GREEN_SOFT;
  const greenText = isDark ? "#CFEFD4" : GREEN;
  const secondaryActionThemedSx = {
    ...secondaryActionButtonSx,
    borderColor: borderSoft,
    color: copperText,
    "&:hover": {
      borderColor: isDark ? "rgba(224,177,132,0.42)" : COPPER,
      bgcolor: isDark ? "rgba(224,177,132,0.12)" : BRAND_COLORS.primary.softSubtle,
    },
  };
  const cardSx = {
    ...cardBaseSx,
    border: `1px solid ${borderSoft}`,
    bgcolor: panelBg,
    color: "text.primary",
  };
  const iconSx = {
    ...iconTileSx,
    bgcolor: isDark ? panelSoftBg : COPPER_SOFT,
    color: copperText,
  };

  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
            Workflow Catalog
          </Typography>
          <Typography variant="body2" sx={{ color: mutedText, mt: 0.35 }}>
            Browse reusable automations by collection.
          </Typography>
        </Box>
        {canCreateWorkflows && (
          <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
            <Button
              component={Link}
              to="/workflow/intent-build"
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              sx={primaryActionButtonSx}
            >
              Create Workflow
            </Button>
          </Stack>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: PAGE_SHELL.sectionGap,
          borderRadius: UI_RADIUS.panel,
          border: `1px solid ${borderSoft}`,
          bgcolor: panelBg,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search workflows by name, purpose, or status..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={formControlSx}
          />
          <FormControl size="small" sx={{ minWidth: 180, ...formControlSx }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={selectedStatus} onChange={(event) => setSelectedStatus(event.target.value)} startAdornment={<InputAdornment position="start"><FilterListIcon /></InputAdornment>}>
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, value) => value && setViewMode(value)}
            sx={{
              flexShrink: 0,
              "& .MuiToggleButton-root": {
                color: mutedText,
                px: 1.3,
                "&.Mui-selected": {
                  bgcolor: copperSurface,
                  color: copperText,
                },
              },
            }}
          >
            <ToggleButton value="grid" aria-label="Grid view"><ViewModuleIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="list" aria-label="List view"><ViewListIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {filteredWorkflows.length === 0 ? (
        <EmptyWorkflowTable
          message={workflows.length === 0 ? "No workflows found. Create one to get started." : "No workflows match the current filters."}
        />
      ) : !activeGroup ? (
        <Box sx={{ display: "grid", gridTemplateColumns: viewMode === "list" ? "1fr" : { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: PAGE_SHELL.sectionGap }}>
          {groups.map((group) => (
            <Card
              key={group.id}
              elevation={0}
              onClick={() => setSelectedCollection(group.id)}
              sx={{
                ...cardSx,
                cursor: "pointer",
                minHeight: viewMode === "list" ? 0 : 200,
                boxShadow: isDark ? "0 18px 54px rgba(0,0,0,0.34)" : "0 12px 28px rgba(74,47,25,0.06)",
                "&:hover": { borderColor: isDark ? "rgba(224,177,132,0.38)" : COPPER, transform: "translateY(-2px)", transition: "180ms ease" },
              }}
            >
              <CardContent sx={{ p: viewMode === "list" ? 2 : 2.6 }}>
                <Stack direction={viewMode === "list" ? "row" : "column"} alignItems={viewMode === "list" ? "center" : "stretch"} justifyContent="space-between" spacing={viewMode === "list" ? 2 : 0}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: UI_RADIUS.panel, ...iconSx }}>
                      <CollectionIcon id={group.id} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 900 }} noWrap={viewMode === "list"}>{group.label}</Typography>
                      <Typography variant="body2" sx={{ color: mutedText, mt: 0.75, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: viewMode === "list" ? 1 : 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{group.description}</Typography>
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    justifyContent={viewMode === "list" ? "flex-end" : "flex-start"}
                    sx={{ mt: viewMode === "list" ? 0 : 2, ml: viewMode === "list" ? 0 : 7.75, flexShrink: 0 }}
                  >
                    {group.activeCount > 0 && (
                      <Chip size="small" label={`${group.activeCount} active`} sx={{ ...chipBaseSx, bgcolor: greenSurface, color: greenText, fontWeight: 800 }} />
                    )}
                    {group.draftCount > 0 && (
                      <Chip size="small" label={`${group.draftCount} draft`} sx={{ ...chipBaseSx, bgcolor: copperSurface, color: copperText, fontWeight: 800 }} />
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>{activeGroup.label}</Typography>
              <Typography variant="body2" sx={{ color: mutedText }}>{activeGroup.workflows.length} workflows in this collection.</Typography>
            </Box>
            <Button onClick={() => setSelectedCollection(null)} variant="outlined" sx={secondaryActionThemedSx}>
              All collections
            </Button>
          </Stack>
          <Box sx={{ display: "grid", gridTemplateColumns: viewMode === "list" ? "1fr" : { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }, gap: PAGE_SHELL.sectionGap }}>
            {visibleWorkflows.map((workflow: Workflow) => {
              const chip = statusChip(workflow, isDark);
              return (
                <Card key={workflow.id} elevation={0} sx={{ ...cardSx, boxShadow: isDark ? "0 18px 54px rgba(0,0,0,0.34)" : "0 10px 26px rgba(31,24,18,0.06)" }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction={viewMode === "list" ? "row" : "column"} spacing={2} justifyContent="space-between" alignItems={viewMode === "list" ? "center" : "stretch"}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Box sx={{ width: 38, height: 38, borderRadius: UI_RADIUS.panel, ...iconSx }}>
                            <AccountTreeOutlinedIcon fontSize="small" />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography component={Link} to={`/workflow/${workflow.id}`} sx={{ color: "text.primary", textDecoration: "none", fontWeight: 900, display: "block" }} noWrap>
                              {workflow.name || workflow.id}
                            </Typography>
                            <Typography variant="caption" sx={{ color: mutedText }}>Version {workflow.version || 1}</Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={0.75} sx={{ mt: 2, ml: 6.25 }} useFlexGap flexWrap="wrap">
                          <Chip size="small" label={chip.label} sx={{ ...chip.sx, fontWeight: 850 }} />
                          {workflow.created_at && <Chip size="small" label={new Date(workflow.created_at).toLocaleDateString()} sx={{ ...chipBaseSx, bgcolor: isDark ? "rgba(255,247,239,0.10)" : "#F1EEE9", color: mutedText, fontWeight: 750 }} />}
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {canRunWorkflows && (
                          <Button component={Link} to={`/workflow/${workflow.id}/run`} startIcon={<PlayArrowIcon />} sx={{ borderRadius: UI_RADIUS.button, color: copperText, textTransform: "none", fontWeight: 900 }}>
                            Run
                          </Button>
                        )}
                        {canUpdateWorkflows && (
                          <Button
                            component={Link}
                            to={`/workflow/${workflow.id}/intent-edit`}
                            state={{ workflow }}
                            aria-label="Edit or refine workflow"
                            startIcon={<AutoAwesomeIcon />}
                            sx={{ borderRadius: UI_RADIUS.button, color: copperText, textTransform: "none", fontWeight: 900 }}
                          >
                            Refine
                          </Button>
                        )}
                        <Button component={Link} to={`/workflow/${workflow.id}`} endIcon={<ArrowForwardIcon />} sx={{ borderRadius: UI_RADIUS.button, color: copperText, textTransform: "none", fontWeight: 900 }}>
                          Open
                        </Button>
                        {canDeleteWorkflows && (
                          <Tooltip title="Delete workflow">
                            <span>
                              <IconButton onClick={() => handleDelete(workflow.id)} disabled={deleting === workflow.id} color="error" size="small" sx={{ borderRadius: UI_RADIUS.iconButton }}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}
